import React, { useState, useContext } from 'react';
import { Form, Button, Alert } from 'reactstrap';
import { Link, useHistory } from 'react-router-dom';
import { reduxForm } from 'redux-form';
import identityClient from 'clients/identityClient';
import BlockingSpinnerOverlay from 'components/BlockingSpinnerOverlay';
import { maskEmail } from '../../../utils/maskingUtils';
import VerificationField from 'components/fields/VerificationField';
import ResendCodeSelection from '../../../components/ResendCodeSelection';
import ErrorAlert from '../PreSignUp/ErrorAlert';
import { error as err, registrationSteps } from '../PreSignUp';
import errorMessages, { toErrorAlertState } from '../../../helpers/errorMessages';
import { isMobileNumberRequiredForLinking } from 'helpers/customBranding';
import HpaStoreContext from '../../../contexts/HpaStoreContext';
import { WORKFLOW_TYPE_LINK_ACCOUNT } from 'helpers/registrationWorkflow';
import { buildSignUpSearchFromLocation } from 'helpers/buildSignUpSearchFromLocation';
import {
  extractHstreamIdFromResponse,
  extractVerifiedContacts,
  isMatchConfirmedLinkFlow,
  mapTelecomToRankedOptions,
  mergeEnteredEmailIntoOptions,
  mergeEnteredPhoneIntoOptions,
  putRegistrationEmailFirst,
  putRegistrationPhoneFirst,
  shouldStopLinkingForPhoneOnAnotherAccount,
} from 'helpers/accountMatchingUtils';

const isLinkAccountWorkflow = (wf) => wf === WORKFLOW_TYPE_LINK_ACCOUNT;

/**
 * Fetches verified emails/phones for a matched account.
 * Returns { emails: [], phones: [] } on failure.
 */
async function fetchAccountContacts(hstreamId) {
  try {
    const accountData = await identityClient.getAccountInfo(hstreamId);
    return extractVerifiedContacts(accountData);
  } catch {
    return { emails: [], phones: [] };
  }
}

/**
 * Builds the navigation state for the SelectPrimaryLogin step.
 */
function buildPrimaryLoginNavState({
  verifiedEmails,
  verifiedPhones,
  effectiveHstreamId,
  email,
  registrationFields,
  phone,
  workflowType,
}) {
  const emailsWithRank = mergeEnteredEmailIntoOptions(
    mapTelecomToRankedOptions(verifiedEmails),
    email,
  );
  const emailsOrdered = putRegistrationEmailFirst(emailsWithRank, email);
  const phonesWithRank = putRegistrationPhoneFirst(
    mergeEnteredPhoneIntoOptions(
      mapTelecomToRankedOptions(verifiedPhones),
      phone,
    ),
    phone,
  );
  const defaultRank1Email =
    emailsOrdered.find((e) => e.rank === 1)?.value || '';

  return {
    emails: emailsOrdered,
    phoneNumbers: phonesWithRank,
    verifyInfo: { hstreamId: effectiveHstreamId },
    isRegistrationFlow: true,
    isAccountMatching: true,
    registrationEmail: email,
    newEmail: email,
    defaultRank1Email,
    workflowType: workflowType || 'NewRegistration',
    isProvisioned: registrationFields?.isProvisioned === true,
    isConfirmed: registrationFields?.isConfirmed === true,
    password: registrationFields.password,
    registrationData: {
      firstName: registrationFields.firstName,
      lastName: registrationFields.lastName,
      email,
      password: registrationFields.password,
      phone,
    },
  };
}

const VerificationCode = ({
  navigate,
  handleSubmit,
  registrationFields,
  workflowType,
}) => {
  const [error, setError] = useState(err);
  const [resent, setResent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isEmailAlreadyUsed, setIsEmailAlreadyUsed] = useState(false);
  const history = useHistory();
  const context = useContext(HpaStoreContext);
  const params = new URLSearchParams(window.location.search);
  const queryString = params.toString();
  const { email, isConfirmed, phone } = registrationFields;
  const isProvisioned = registrationFields?.isProvisioned === true;

  const signInSearch = buildSignUpSearchFromLocation(
    typeof window !== 'undefined' ? window.location.search : '',
    { username: email },
  );
  const hStreamId =
    registrationFields?.hStreamId ?? registrationFields?.hstreamId ?? '';

  const codeSentMsg = phone
    ? `Please enter the Verification Code ${resent ? 'resent' : 'sent'
    } to your mobile number ${phone.substring(0, 2) + 'XXXXXX' + phone.substring(8)
    }, if the phone number exists.`
    : `A verification code has been ${resent ? 're' : ''}sent to ${maskEmail(
      email,
    )}, if the email exists.`;

  const showError = (message) =>
    setError({ errorFound: true, errorMessage: message });

  const resolveIsMobileRequiredForLink = () =>
    registrationFields?.isMobileRequired === true ||
    isMobileNumberRequiredForLinking(context, params);

  const linkMatchConfirmedAccount = async () => {
    try {
      const linkUserResponse = await identityClient.linkUser(
        hStreamId,
        email,
        queryString,
      );
      navigate(registrationSteps.SUCCESS, {
        code: linkUserResponse?.code,
      });
    } catch {
      history.push({
        pathname: '/account/linkError',
        search: queryString,
      });
    }
  };

  const navigateToPrimaryLoginOrSuccess = (
    verifiedEmails,
    verifiedPhones,
    effectiveHstreamId,
  ) => {
    const total = verifiedEmails.length + verifiedPhones.length;
    const hasRegistrationContact = !!(email || phone);
    if (
      total === 0 &&
      !registrationFields?.matchedHstreamId &&
      !hasRegistrationContact
    ) {
      navigate(registrationSteps.SUCCESS, {
        isFromRegistration: true,
        accountMatched: true,
      });
      return;
    }
    navigate(
      registrationSteps.SELECTPRIMARYLOGIN,
      buildPrimaryLoginNavState({
        verifiedEmails,
        verifiedPhones,
        effectiveHstreamId,
        email,
        registrationFields,
        phone,
        workflowType,
      }),
    );
  };

  /**
   * Handles the new-registration path where firstName, password, and phone
   * are all present (phone OTP verified, ready to register).
   */
  const handlePhoneRegistrationPath = async ({
    effectiveHstreamId,
    isPhoneAccountMatch,
    verifiedEmails,
    verifiedPhones,
    allowEmptyVerifyResponse,
  }) => {

    if (
      !effectiveHstreamId &&
      !allowEmptyVerifyResponse &&
      !isLinkAccountWorkflow(workflowType)
    ) {
      showError('Verification failed. Please try again.');
      return;
    }

    if (isPhoneAccountMatch && effectiveHstreamId) {
      navigateToPrimaryLoginOrSuccess(
        verifiedEmails,
        verifiedPhones,
        effectiveHstreamId,
      );
      return;
    }

    let hstreamId;
    try {
      const registerResponse = await identityClient.registerUser(
        registrationFields.firstName,
        registrationFields.lastName,
        email,
        registrationFields.password,
        phone,
        workflowType,
      );
      hstreamId = registerResponse.accountId;
    } catch (registerError) {
      const detail = registerError.response?.data?.detail;
      const status = registerError.response?.status;
      const isConfirmedPersonConflict =
        !isLinkAccountWorkflow(workflowType) &&
        status === 409 &&
        (detail === 'Confirmed person record is available' ||
          detail === 'Multiple confirmed person records are available');
      if (isConfirmedPersonConflict && isPhoneAccountMatch) {
        navigateToPrimaryLoginOrSuccess(
          verifiedEmails,
          verifiedPhones,
          effectiveHstreamId,
        );
        return;
      }
      showError(detail || 'An error occurred while processing your request.');
      return;
    }

    if (isLinkAccountWorkflow(workflowType)) {
      try {
        const linkUserResponse = await identityClient.linkUser(
          hstreamId,
          email,
          queryString,
        );
        navigate(registrationSteps.SUCCESS, {
          code: linkUserResponse?.code,
        });
      } catch {
        history.push({
          pathname: '/account/linkError',
          search: queryString,
        });
      }
    } else {
      navigate(registrationSteps.SUCCESS);
    }
  };

  /**
   * Handles the MATCHCONFIRMED + LinkAccount path (MatchFound):
   * email verified -> mobile prompt (if needed) -> mobile verify -> setAlias -> link success
   */
  const handleMatchConfirmedLinkPath = async () => {
    const isMobileRequiredForLink = resolveIsMobileRequiredForLink();

    if (
      !phone &&
      isMobileRequiredForLink &&
      !registrationFields.mobileNumberExists
    ) {
      navigate(registrationSteps.MOBILEPROMPT, {
        email,
        hStreamId,
        workflowType,
        isConfirmed,
        isProvisioned: false,
        firstName: registrationFields.firstName,
        lastName: registrationFields.lastName,
        isMobileRequired: isMobileRequiredForLink,
        mobileNumberExists: registrationFields.mobileNumberExists,
      });
      return;
    }

    if (phone) {
      try {
        await identityClient.SetAlias(hStreamId, 'phone', phone);
      } catch {
        showError(
          'An error occurred while setting up your mobile number.',
        );
        return;
      }
    }

    await linkMatchConfirmedAccount();
  };

  /**
   * Handles the PROVISIONED + LinkAccount path when isConfirmed is true.
   */
  const handleProvisionedLinkPath = async () => {
    try {
      const linkUserResponse = await identityClient.linkUser(
        hStreamId,
        email,
        queryString,
      );
      navigate(registrationSteps.SUCCESS, {
        code: linkUserResponse?.code,
      });
    } catch {
      history.push({
        pathname: '/account/linkError',
        search: queryString,
      });
    }
  };

  const matchedHstreamId =
    registrationFields?.matchedHstreamId ??
    registrationFields?.matchedHStreamId ??
    '';

  const onContinue = async (verification) => {
    setIsLoading(true);
    try {
      const allowEmptyVerifyResponse =
        !!phone &&
        !!registrationFields.firstName &&
        !!registrationFields.password;

      const verifyResponse = await identityClient.verifyRegistrationOtp(
        hStreamId,
        email,
        verification,
        phone,
        workflowType,
        allowEmptyVerifyResponse,
      );

      const accountMatchingFromVerify =
        !!phone && verifyResponse?.accountMatchingDetected === true;
      const isPhoneAccountMatch =
        !!phone &&
        (!!registrationFields?.matchedHstreamId || accountMatchingFromVerify);
      const matchedIdFromVerify = extractHstreamIdFromResponse(verifyResponse);

      const effectiveHstreamId = isPhoneAccountMatch
        ? matchedHstreamId || matchedIdFromVerify || hStreamId
        : matchedIdFromVerify || hStreamId;

      const isMatchConfirmedLink = isMatchConfirmedLinkFlow(
        workflowType,
        isConfirmed,
        isProvisioned,
      );

      if (
        shouldStopLinkingForPhoneOnAnotherAccount({
          isPhoneAccountMatch,
          isProvisioned,
          isConfirmed,
          workflowType,
        })
      ) {
        setError({
          errorFound: true,
          errorMessage: errorMessages.phoneOnAnotherAccountLinking.message,
          errorType: errorMessages.phoneOnAnotherAccountLinking.type,
        });
        return;
      }

      let verifiedEmails = [];
      let verifiedPhones = [];
      if (isPhoneAccountMatch && effectiveHstreamId) {
        const contacts = await fetchAccountContacts(effectiveHstreamId);
        verifiedEmails = contacts.emails;
        verifiedPhones = contacts.phones;
      }

      if (registrationFields.firstName && registrationFields.password && phone) {
        await handlePhoneRegistrationPath({
          effectiveHstreamId,
          isPhoneAccountMatch,
          verifiedEmails,
          verifiedPhones,
          allowEmptyVerifyResponse,
        });
        return;
      }

      if (isMatchConfirmedLink) {
        await handleMatchConfirmedLinkPath();
        return;
      }

      if (
        isProvisioned &&
        isLinkAccountWorkflow(workflowType) &&
        isConfirmed
      ) {
        const needsRegistrationForMobile =
          resolveIsMobileRequiredForLink() &&
          !registrationFields.mobileNumberExists &&
          !phone &&
          !registrationFields.password;

        if (needsRegistrationForMobile) {
          navigate(registrationSteps.REGISTRATION, {
            ...registrationFields,
            hStreamId,
            isMobileRequired: true,
            workflowType,
            isProvisioned: true,
          });
          return;
        }

        await handleProvisionedLinkPath();
        return;
      }

      if (isConfirmed) {
        navigate(registrationSteps.SUCCESS);
      } else {
        navigate(registrationSteps.REGISTRATION, {
          ...registrationFields,
          hStreamId:
            effectiveHstreamId ||
            registrationFields?.hStreamId ||
            registrationFields?.hstreamId ||
            '',
        });
      }
    } catch (e) {
      if (e.status === 409) {
        if (!phone) {
          setIsEmailAlreadyUsed(true);
        }
        setError(toErrorAlertState(e.response?.data?.detail));
      } else {
        setError({
          errorFound: true,
          errorMessage:
            e.status === 400
              ? errorMessages?.invalidCode?.message
              : errorMessages?.genericError?.message,
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailAlreadyUsedContinue = () => {
    if (isLinkAccountWorkflow(workflowType)) {
      const params = new URLSearchParams(window.location.search);
      const redirectUri = params.get('redirect_uri');

      if (redirectUri) {
        params.delete('client_id');
        params.delete('redirect_uri');
        params.delete('response_type');
        params.delete('firstname');
        params.delete('lastname');
        params.delete('username');
        const queryString = params.toString();
        if (queryString) {
          window.location.href = `${redirectUri}?error=account_already_linked&${queryString}`;
        } else {
          window.location.href = `${redirectUri}?error=account_already_linked`;
        }
      }
    } else {
      history.push({
        pathname: '/account/signup',
        search: window.location.search,
      });
    }
  };

  const onResend = async () => {
    setIsSendingOtp(true);
    const emailToSend = phone ? '' : registrationFields.email;
    try {
      await identityClient.sendRegistrationOTP(
        emailToSend,
        registrationFields.hStreamId ?? registrationFields.hstreamId,
        phone,
        workflowType,
      );
      setError({ errorFound: false });
      setResent(true);
    } catch (e) {
      if (e.status === 409) {
        setError(toErrorAlertState(e.response?.data?.detail));
      } else {
        setError({
          errorFound: true,
          errorMessage: errorMessages.resendCode.message,
        });
      }
    } finally {
      setIsSendingOtp(false);
    }
  };

  const isMatchConfirmedLinkAccount = () =>
    isMatchConfirmedLinkFlow(workflowType, isConfirmed, isProvisioned);

  const onCancel = () => {
    setError({ errorFound: false, errorMessage: '' });
    setResent(false);
    setIsLoading(false);
    setIsSendingOtp(false);
    registrationFields.password = '';
    if (isMatchConfirmedLinkAccount()) {
      navigate(registrationSteps.MOBILEPROMPT, registrationFields);
    } else {
      navigate(registrationSteps.REGISTRATION, registrationFields);
    }
  };

  const isVerificationInProgress = isLoading && !isEmailAlreadyUsed;
  const showBlockingOverlay = isVerificationInProgress || isSendingOtp;

  return (
    <>
      <div
        className="position-relative"
        aria-busy={showBlockingOverlay || undefined}
      >
        <BlockingSpinnerOverlay
          show={showBlockingOverlay}
          ariaLabel={
            isVerificationInProgress
              ? 'Verifying, please wait'
              : 'Sending verification code, please wait'
          }
        />
        <Form
          className="AuthWrapper__register"
          onSubmit={handleSubmit((values) => {
            const verification = values.verification;
            onContinue(verification);
          })}
        >
          {error.errorFound ? (
            <div>
              {isEmailAlreadyUsed ? (
                <Alert
                  color="danger"
                  className="d-flex align-items-center justify-content-between"
                >
                  <span>{error.errorMessage}</span>
                  <Button
                    id="emailAlreadyUsedContinue"
                    color="danger"
                    onClick={handleEmailAlreadyUsedContinue}
                  >
                    Continue
                  </Button>
                </Alert>
              ) : (
                <ErrorAlert error={error} />
              )}
            </div>
          ) : (
            <Alert className="AuthWrapper__register__wordbreak">
              {codeSentMsg}
            </Alert>
          )}

          <VerificationField
            label="Verification Code"
            name="verification"
            required
            disabled={isEmailAlreadyUsed || isLoading || isSendingOtp}
          />
          <div className="d-flex" style={{ gap: '10px' }}>
            <Button
              id="verification"
              type="submit"
              disabled={isLoading || isSendingOtp || isEmailAlreadyUsed}
              color="primary"
              style={{ flex: 1 }}
            >
              {isLoading ? 'Loading...' : 'Continue'}
            </Button>
            {phone && (
              <Button
                id="verificationCancel"
                type="button"
                disabled={isLoading || isSendingOtp}
                color="link"
                onClick={onCancel}
                style={{ flex: 1 }}
              >
                Cancel
              </Button>
            )}
          </div>
        </Form>

        {!isEmailAlreadyUsed && (
          <div className="AuthWrapper__content-message">
            <hr />
            {phone ? (
              <div className="d-flex justify-content-start align-items-center flex-wrap">
                <b style={{ marginRight: '4px' }}>Need a new code?</b>
                <div className="d-flex justify-content-start align-items-center">
                  <span>Resend to your</span>
                  <Button
                    color="link"
                    size="sm"
                    type="button"
                    onClick={onResend}
                    disabled={isLoading || isSendingOtp}
                    style={{ padding: 0, margin: '0 4px', fontSize: '1em' }}
                    id="send-code-phone"
                  >
                    Phone
                  </Button>
                </div>
              </div>
            ) : (
              <ResendCodeSelection
                resendCode={onResend}
                email={registrationFields.email}
                disabled={isLoading}
                isSending={isSendingOtp}
              />
            )}
            {isLinkAccountWorkflow(workflowType) || phone ? (
              <></>
            ) : (
              <div>
                <b>Already verified?</b>{' '}
                <Link
                  to={{
                    pathname: '/login',
                    search: signInSearch,
                    ...(email ? { state: { username: email } } : {}),
                  }}
                >
                  Sign In
                </Link>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default reduxForm({
  form: 'verificationCode',
})(VerificationCode);

export { VerificationCode as PureVerificationCodeForm };
